import { Link2, Layers, Bell, CheckCircle } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: Link2,
    title: 'Connect Your Data',
    description: 'Integrate with your FCMs, exchanges, and trading systems. We support all major platforms and custom connections.',
  },
  {
    number: '02',
    icon: Layers,
    title: 'Aggregate Positions',
    description: 'Automatically aggregate positions across all accounts per CFTC aggregation rules. No more manual spreadsheet work.',
  },
  {
    number: '03',
    icon: Bell,
    title: 'Monitor Limits',
    description: 'Real-time monitoring with configurable alert thresholds at 70%, 90%, and 100% of position limits.',
  },
  {
    number: '04',
    icon: CheckCircle,
    title: 'Stay Compliant',
    description: 'Receive instant alerts, generate audit reports, and maintain a complete compliance trail automatically.',
  },
];

export function HowItWorksSection() {
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="how-it-works" className="relative py-20 md:py-28 bg-white overflow-hidden">
      <div className="container relative">
        {/* Section Header */}
        <div className="mx-auto max-w-2xl text-center mb-16">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4 text-slate-900">
            How{' '}
            <span className="text-purple-600">
              PositionIQ
            </span>{' '}
            Works
          </h2>
          <p className="text-lg text-slate-600">
            From integration to compliance in four simple steps. Get up and running in days, not months.
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connector Line (desktop) */}
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-purple-200 rounded-full" />

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((step, index) => (
              <div key={step.number} className="relative">
                {/* Step Card */}
                <div className="group rounded-2xl bg-white border border-slate-200 p-6 h-full hover:border-purple-300 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 relative z-10">
                  {/* Step Number */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-4xl font-bold text-purple-600 opacity-30 group-hover:opacity-100 transition-opacity">
                      {step.number}
                    </span>
                    <div className="inline-flex rounded-xl bg-purple-600 p-3 shadow-lg group-hover:scale-110 transition-transform">
                      <step.icon className="h-5 w-5 text-white" />
                    </div>
                  </div>

                  <h3 className="font-semibold text-lg mb-2 text-slate-900">{step.title}</h3>
                  <p className="text-slate-600 text-sm">
                    {step.description}
                  </p>
                </div>

                {/* Arrow connector (mobile/tablet) */}
                {index < steps.length - 1 && (
                  <div className="flex justify-center lg:hidden my-4">
                    <svg className="w-6 h-6 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-slate-600 mb-4">
            Ready to simplify your compliance workflow?
          </p>
          <a
            href="#demo"
            onClick={(e) => scrollToSection(e, '#demo')}
            className="inline-flex items-center justify-center rounded-full bg-purple-600 px-8 py-4 text-base font-semibold text-white hover:bg-purple-700 transition-colors"
          >
            Schedule a Demo
          </a>
        </div>
      </div>
    </section>
  );
}
